/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.4.0 - Timezone Utilities
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file timezoneUtils.ts
 * @description Utilidades para manejo de zonas horarias
 * @version 1.0.0
 * @date 2025-01-27
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { TimeZoneInfo } from './scheduler.types';

// ═══════════════════════════════════════════════════════════════════════════
// LISTADO DE ZONAS HORARIAS PRINCIPALES
// ═══════════════════════════════════════════════════════════════════════════

export const TIMEZONES: TimeZoneInfo[] = [
  // AMÉRICAS
  { 
    value: 'America/Santiago', 
    label: '(UTC-3) Santiago', 
    offset: '-03:00', 
    offsetMinutes: -180,
    abbr: 'CLST',
    region: 'americas'
  },
  { 
    value: 'America/Buenos_Aires', 
    label: '(UTC-3) Buenos Aires', 
    offset: '-03:00', 
    offsetMinutes: -180,
    abbr: 'ART',
    region: 'americas'
  },
  { 
    value: 'America/Sao_Paulo', 
    label: '(UTC-3) São Paulo', 
    offset: '-03:00', 
    offsetMinutes: -180,
    abbr: 'BRT',
    region: 'americas'
  },
  { 
    value: 'America/Montevideo', 
    label: '(UTC-3) Montevideo', 
    offset: '-03:00', 
    offsetMinutes: -180,
    abbr: 'UYT',
    region: 'americas'
  },
  { 
    value: 'America/Bogota', 
    label: '(UTC-5) Bogotá', 
    offset: '-05:00', 
    offsetMinutes: -300,
    abbr: 'COT',
    region: 'americas'
  },
  { 
    value: 'America/Lima', 
    label: '(UTC-5) Lima', 
    offset: '-05:00', 
    offsetMinutes: -300,
    abbr: 'PET',
    region: 'americas'
  },
  { 
    value: 'America/New_York', 
    label: '(UTC-5) Nueva York', 
    offset: '-05:00', 
    offsetMinutes: -300,
    abbr: 'EST',
    region: 'americas'
  },
  { 
    value: 'America/Chicago', 
    label: '(UTC-6) Chicago', 
    offset: '-06:00', 
    offsetMinutes: -360,
    abbr: 'CST',
    region: 'americas'
  },
  { 
    value: 'America/Mexico_City', 
    label: '(UTC-6) Ciudad de México', 
    offset: '-06:00', 
    offsetMinutes: -360,
    abbr: 'CST',
    region: 'americas'
  },
  { 
    value: 'America/Denver', 
    label: '(UTC-7) Denver', 
    offset: '-07:00', 
    offsetMinutes: -420,
    abbr: 'MST',
    region: 'americas'
  },
  { 
    value: 'America/Los_Angeles', 
    label: '(UTC-8) Los Angeles', 
    offset: '-08:00', 
    offsetMinutes: -480,
    abbr: 'PST',
    region: 'americas'
  },
  
  // EUROPA
  { 
    value: 'Europe/London', 
    label: '(UTC+0) Londres', 
    offset: '+00:00', 
    offsetMinutes: 0,
    abbr: 'GMT',
    region: 'europe'
  },
  { 
    value: 'Europe/Lisbon', 
    label: '(UTC+0) Lisboa', 
    offset: '+00:00', 
    offsetMinutes: 0,
    abbr: 'WET',
    region: 'europe'
  },
  { 
    value: 'Europe/Madrid', 
    label: '(UTC+1) Madrid', 
    offset: '+01:00', 
    offsetMinutes: 60,
    abbr: 'CET',
    region: 'europe'
  },
  { 
    value: 'Europe/Paris', 
    label: '(UTC+1) París', 
    offset: '+01:00', 
    offsetMinutes: 60,
    abbr: 'CET',
    region: 'europe'
  },
  { 
    value: 'Europe/Berlin', 
    label: '(UTC+1) Berlín', 
    offset: '+01:00', 
    offsetMinutes: 60,
    abbr: 'CET',
    region: 'europe'
  },
  { 
    value: 'Europe/Rome', 
    label: '(UTC+1) Roma', 
    offset: '+01:00', 
    offsetMinutes: 60,
    abbr: 'CET',
    region: 'europe'
  },
  { 
    value: 'Europe/Athens', 
    label: '(UTC+2) Atenas', 
    offset: '+02:00', 
    offsetMinutes: 120,
    abbr: 'EET',
    region: 'europe'
  },
  { 
    value: 'Europe/Moscow', 
    label: '(UTC+3) Moscú', 
    offset: '+03:00', 
    offsetMinutes: 180,
    abbr: 'MSK',
    region: 'europe'
  },
  
  // ASIA
  { 
    value: 'Asia/Dubai', 
    label: '(UTC+4) Dubái', 
    offset: '+04:00', 
    offsetMinutes: 240,
    abbr: 'GST',
    region: 'asia'
  },
  { 
    value: 'Asia/Kolkata', 
    label: '(UTC+5:30) Mumbai', 
    offset: '+05:30', 
    offsetMinutes: 330,
    abbr: 'IST',
    region: 'asia'
  },
  { 
    value: 'Asia/Bangkok', 
    label: '(UTC+7) Bangkok', 
    offset: '+07:00', 
    offsetMinutes: 420,
    abbr: 'ICT',
    region: 'asia'
  },
  { 
    value: 'Asia/Singapore', 
    label: '(UTC+8) Singapur', 
    offset: '+08:00', 
    offsetMinutes: 480,
    abbr: 'SGT',
    region: 'asia'
  },
  { 
    value: 'Asia/Shanghai', 
    label: '(UTC+8) Shanghái', 
    offset: '+08:00', 
    offsetMinutes: 480,
    abbr: 'CST',
    region: 'asia'
  },
  { 
    value: 'Asia/Tokyo', 
    label: '(UTC+9) Tokio', 
    offset: '+09:00', 
    offsetMinutes: 540,
    abbr: 'JST',
    region: 'asia'
  },
  { 
    value: 'Asia/Seoul', 
    label: '(UTC+9) Seúl', 
    offset: '+09:00', 
    offsetMinutes: 540,
    abbr: 'KST',
    region: 'asia'
  },
  
  // OCEANÍA
  { 
    value: 'Australia/Sydney', 
    label: '(UTC+11) Sídney', 
    offset: '+11:00', 
    offsetMinutes: 660,
    abbr: 'AEDT',
    region: 'oceania'
  },
  { 
    value: 'Australia/Melbourne', 
    label: '(UTC+11) Melbourne', 
    offset: '+11:00', 
    offsetMinutes: 660,
    abbr: 'AEDT',
    region: 'oceania'
  },
  { 
    value: 'Pacific/Auckland', 
    label: '(UTC+13) Auckland', 
    offset: '+13:00', 
    offsetMinutes: 780,
    abbr: 'NZDT',
    region: 'oceania'
  },
  
  // ÁFRICA
  { 
    value: 'Africa/Lagos', 
    label: '(UTC+1) Lagos', 
    offset: '+01:00', 
    offsetMinutes: 60,
    abbr: 'WAT',
    region: 'africa'
  },
  { 
    value: 'Africa/Johannesburg', 
    label: '(UTC+2) Johannesburgo', 
    offset: '+02:00', 
    offsetMinutes: 120,
    abbr: 'SAST',
    region: 'africa'
  },
  { 
    value: 'Africa/Cairo', 
    label: '(UTC+2) El Cairo', 
    offset: '+02:00', 
    offsetMinutes: 120,
    abbr: 'EET',
    region: 'africa'
  }
];

// ═══════════════════════════════════════════════════════════════════════════
// FUNCIONES PRINCIPALES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Detecta la zona horaria del usuario
 */
export const detectUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Santiago';
  } catch (error) {
    console.warn('Could not detect user timezone, defaulting to America/Santiago');
    return 'America/Santiago';
  }
};

/**
 * Obtiene información completa de una zona horaria
 */
export const getTimezoneInfo = (timezone: string): TimeZoneInfo | undefined => {
  return TIMEZONES.find(tz => tz.value === timezone);
};

/**
 * Convierte una fecha a una zona horaria específica
 */
export const convertToTimezone = (date: Date, timezone: string): string => {
  try {
    return date.toLocaleString('es-CL', { 
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (error) {
    console.error('Error converting to timezone:', error);
    return date.toLocaleString('es-CL');
  }
};

/**
 * Obtiene solo la hora en una zona horaria específica
 */
export const getTimeInTimezone = (date: Date, timezone: string): string => {
  try {
    return date.toLocaleTimeString('es-CL', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error getting time in timezone:', error);
    return date.toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

/**
 * Obtiene la fecha actual en una zona horaria específica
 */
export const getCurrentDateInTimezone = (timezone: string): Date => {
  const now = new Date();
  const str = now.toLocaleString('en-US', { timeZone: timezone });
  return new Date(str);
};

/**
 * Calcula el offset en minutos entre dos zonas horarias
 */
export const getTimezoneOffset = (timezone: string): number => {
  const info = getTimezoneInfo(timezone);
  return info ? info.offsetMinutes : 0;
};

/**
 * Obtiene la diferencia horaria entre dos zonas
 */
export const getTimezoneDifference = (timezone1: string, timezone2: string): number => {
  const offset1 = getTimezoneOffset(timezone1);
  const offset2 = getTimezoneOffset(timezone2);
  return offset2 - offset1;
};

// ═══════════════════════════════════════════════════════════════════════════
// FORMATEO Y VISUALIZACIÓN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Formatea el offset de zona horaria (e.g., "-03:00")
 */
export const formatTimezoneOffset = (offsetMinutes: number): string => {
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes >= 0 ? '+' : '-';
  
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Obtiene el label completo de una zona horaria
 */
export const getTimezoneLabel = (timezone: string): string => {
  const info = getTimezoneInfo(timezone);
  return info ? info.label : timezone;
};

/**
 * Obtiene zonas horarias por región
 */
export const getTimezonesByRegion = (region: 'americas' | 'europe' | 'asia' | 'oceania' | 'africa'): TimeZoneInfo[] => {
  return TIMEZONES.filter(tz => tz.region === region);
};

/**
 * Busca zonas horarias por texto
 */
export const searchTimezones = (query: string): TimeZoneInfo[] => {
  const lowerQuery = query.toLowerCase();
  return TIMEZONES.filter(tz => 
    tz.label.toLowerCase().includes(lowerQuery) ||
    tz.value.toLowerCase().includes(lowerQuery) ||
    tz.abbr.toLowerCase().includes(lowerQuery)
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CONVERSIÓN DE FECHA/HORA CON TIMEZONE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Crea una fecha UTC desde fecha local y zona horaria
 */
export const createUTCFromLocal = (localDate: string, localTime: string, timezone: string): Date => {
  // Combinar fecha y hora local
  const localDateTimeString = `${localDate}T${localTime}:00`;
  
  // Crear fecha en la zona horaria especificada
  const dateInTimezone = new Date(localDateTimeString);
  
  // Obtener el offset de la zona horaria
  const offset = getTimezoneOffset(timezone);
  
  // Ajustar a UTC
  const utcDate = new Date(dateInTimezone.getTime() - (offset * 60 * 1000));
  
  return utcDate;
};

/**
 * Convierte fecha UTC a fecha/hora local en una zona horaria
 */
export const convertUTCToLocal = (utcDate: Date, timezone: string): { date: string; time: string } => {
  const localDateStr = utcDate.toLocaleDateString('en-CA', { timeZone: timezone }); // YYYY-MM-DD
  const localTimeStr = utcDate.toLocaleTimeString('en-GB', { 
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }); // HH:mm
  
  return {
    date: localDateStr,
    time: localTimeStr
  };
};

/**
 * Valida si una zona horaria es válida
 */
export const isValidTimezone = (timezone: string): boolean => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (error) {
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS PARA SCHEDULER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Genera preview de hora programada en múltiples zonas
 */
export const generateSchedulePreview = (
  localDate: string, 
  localTime: string, 
  timezone: string
): string => {
  try {
    const utcDate = createUTCFromLocal(localDate, localTime, timezone);
    const tzInfo = getTimezoneInfo(timezone);
    
    if (!tzInfo) return 'Zona horaria inválida';
    
    // Formato: "Lun 29 Ene 2025 a las 09:00 (Santiago)"
    const dateObj = new Date(localDate);
    const dayName = dateObj.toLocaleDateString('es-CL', { weekday: 'short' });
    const day = dateObj.getDate();
    const monthName = dateObj.toLocaleDateString('es-CL', { month: 'short' });
    const year = dateObj.getFullYear();
    
    const cityName = tzInfo.label.split(')')[1].trim();
    
    return `${dayName} ${day} ${monthName} ${year} a las ${localTime} (${cityName})`;
  } catch (error) {
    console.error('Error generating schedule preview:', error);
    return 'Error en preview';
  }
};

/**
 * Valida si una hora programada es válida (no en el pasado)
 */
export const isValidScheduleTime = (
  localDate: string, 
  localTime: string, 
  timezone: string
): { valid: boolean; message?: string } => {
  try {
    const scheduledUTC = createUTCFromLocal(localDate, localTime, timezone);
    const now = new Date();
    
    if (scheduledUTC < now) {
      return {
        valid: false,
        message: 'La fecha y hora programadas ya pasaron'
      };
    }
    
    // Warning si es muy pronto (menos de 5 minutos)
    const minutesUntil = (scheduledUTC.getTime() - now.getTime()) / 1000 / 60;
    if (minutesUntil < 5) {
      return {
        valid: true,
        message: 'Advertencia: El envío está programado para muy pronto (menos de 5 minutos)'
      };
    }
    
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      message: 'Error al validar la hora programada'
    };
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export default {
  TIMEZONES,
  detectUserTimezone,
  getTimezoneInfo,
  convertToTimezone,
  getTimeInTimezone,
  getCurrentDateInTimezone,
  getTimezoneOffset,
  getTimezoneDifference,
  formatTimezoneOffset,
  getTimezoneLabel,
  getTimezonesByRegion,
  searchTimezones,
  createUTCFromLocal,
  convertUTCToLocal,
  isValidTimezone,
  generateSchedulePreview,
  isValidScheduleTime
};
